import { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router";
import { useQuery, useMutation } from "@apollo/client";
import type { InternalRefetchQueriesInclude } from "@apollo/client";
import {
  Mail,
  Phone,
  Building2,
  Briefcase,
  Pencil,
  Archive,
  ArchiveRestore,
  X,
  Check,
  Plus,
  Trash2,
  MessageSquare,
  Calendar,
  CreditCard,
  AlertCircle,
  Users,
  XCircle,
  CheckCircle,
  UserPlus,
  RotateCcw,
  ArrowRightLeft,
  FileCheck,
  FileText,
  Merge,
  Scissors,
} from "lucide-react";
import { Avatar, Badge, FeeAgreementViewer, PageHeader, TagEditor } from "../../components/ui";
import type { TagItem } from "../../components/ui";
import { useToastPush } from "../../components/ui/Toast";
import { useAuth } from "../../auth/AuthProvider";
import { ManualSessionModal } from "../sessions/ManualSessionModal";
import { AddClientModal } from "./AddClientModal";
import { ClientPickerModal } from "./ClientPickerModal";
import {
  GET_CLIENT_DETAIL,
  GET_CLIENT_NOTES,
  GET_ALL_PERSON_TAGS,
  UPDATE_CLIENT,
  UPDATE_PERSON,
  MOVE_PERSON,
  SPLIT_PERSON,
  MERGE_CLIENTS,
  REMOVE_PERSON_FROM_CLIENT,
  SET_PRIMARY_PERSON,
  CREATE_PERSON_NOTE,
  UPDATE_PERSON_NOTE,
  DELETE_PERSON_NOTE,
} from "../../apollo/clients";
import {
  GET_BOOKING_PAYMENTS,
  GET_BOOKING_MOVES,
  GET_BOOKING_CLOSURES,
  GET_BOOKING_SIGNATURES,
} from "../../apollo/sessions";
import {
  deriveStatus,
  deriveActivityTimeline,
  groupEventsByDay,
  countByCategory,
  getDisplayName,
  getSecondaryName,
  getInitials,
  primaryContact,
  formatSessionLabel,
  formatCurrency,
  CLIENT_STATUS_LABEL,
  CLIENT_STATUS_COLOR,
} from "../../data/clientDerive";
import { timeZoneAbbr } from "../../utils/sessionTime";
import type {
  DetailClient,
  PersonRow,
  ClientStatus,
  ClientNote,
  RawPayment,
  RawBookingMove,
  RawBookingClosure,
  RawBookingSignature,
  ActivityEvent,
} from "../../data/clientDerive";

// ── Types ────────────────────────────────────────────────────────────

type ActivityFilter = "all" | "sessions" | "payments" | "agreements" | "notes";

const NOTE_MAX_CHARS = 5000;

// ── Icon map ─────────────────────────────────────────────────────────

const EVENT_ICONS = {
  Calendar,
  XCircle,
  CheckCircle,
  MessageSquare,
  UserPlus,
  Archive,
  CreditCard,
  AlertCircle,
  RotateCcw,
  ArrowRightLeft,
  FileCheck,
} as const;

const EVENT_ICON_COLORS: Record<ActivityEvent["icon"], string> = {
  Calendar: "text-tide",
  XCircle: "text-rust",
  CheckCircle: "text-sage",
  MessageSquare: "text-spark",
  UserPlus: "text-ink-4",
  Archive: "text-ink-4",
  CreditCard: "text-sage",
  AlertCircle: "text-rust",
  RotateCcw: "text-ink-4",
  ArrowRightLeft: "text-tide",
  FileCheck: "text-sage",
};

// ── Helpers ──────────────────────────────────────────────────────────

function formatMemberSince(isoDate: string): string {
  const d = new Date(isoDate);
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

function formatEventTime(isoDate: string): string {
  const d = new Date(isoDate);
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
  return `${time} ${timeZoneAbbr(d)}`;
}

function StatusPill({ status }: { status: ClientStatus }) {
  const cfg = CLIENT_STATUS_COLOR[status];
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${cfg.text}`}
    >
      <div className={`dot ${cfg.dot}`} />
      {CLIENT_STATUS_LABEL[status]}
    </div>
  );
}

// ── Person card ─────────────────────────────────────────────────────

interface PersonCardProps {
  person: PersonRow;
  clientId: string;
  tagVocabulary: TagItem[];
  onMove: (person: PersonRow) => void;
  onSplit: (person: PersonRow) => void;
  onRemove: (person: PersonRow) => void;
  refetchQueries: InternalRefetchQueriesInclude;
}

function PersonCard({
  person,
  clientId,
  tagVocabulary,
  onMove,
  onSplit,
  onRemove,
  refetchQueries,
}: PersonCardProps) {
  const toast = useToastPush();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [business, setBusiness] = useState("");
  const [title, setTitle] = useState("");

  const [updatePerson, { loading: saving }] = useMutation(UPDATE_PERSON, {
    refetchQueries,
  });
  const [setPrimaryMut, { loading: settingPrimary }] = useMutation(SET_PRIMARY_PERSON, {
    refetchQueries,
  });

  const memberships = person.clients ?? [];
  // is_primary is per-CLIENT: this person fronts THIS client.
  const isPrimaryHere =
    memberships.find((m) => m.id === clientId)?.isPrimary ?? false;
  const otherClients = memberships.filter((m) => m.id !== clientId);
  const onlyMembership = memberships.length <= 1;

  const enterEdit = useCallback(() => {
    setName(person.name ?? "");
    setEmail(person.email);
    setPhone(person.phoneNumber ?? "");
    setBusiness(person.businessName ?? "");
    setTitle(person.title ?? "");
    setIsEditing(true);
  }, [person]);

  const save = useCallback(async () => {
    if (!name.trim() || !email.trim()) {
      toast({ kind: "error", title: "Name and email are required." });
      return;
    }
    try {
      await updatePerson({
        variables: {
          id: person.id,
          name: name.trim(),
          email: email.trim(),
          phoneNumber: phone.trim() || null,
          businessName: business.trim() || null,
          title: title.trim() || null,
        },
      });
      setIsEditing(false);
      toast({ kind: "success", title: "Person updated" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to save", description: (err as Error).message });
    }
  }, [person.id, name, email, phone, business, title, updatePerson, toast]);

  const handleTagsChange = useCallback(
    (tagNames: string[]) => {
      updatePerson({ variables: { id: person.id, tagNames } }).catch((err) => {
        toast({
          kind: "error",
          title: "Couldn't update tags",
          description: (err as Error).message,
        });
      });
    },
    [person.id, updatePerson, toast],
  );

  const handleArchiveToggle = useCallback(async () => {
    const archiving = !person.archivedAt;
    try {
      await updatePerson({
        variables: {
          id: person.id,
          archivedAt: archiving ? new Date().toISOString() : null,
        },
      });
      toast({
        kind: "success",
        title: archiving ? "Person archived" : "Person restored",
      });
    } catch (err) {
      toast({
        kind: "error",
        title: archiving ? "Failed to archive person" : "Failed to restore person",
        description: (err as Error).message,
      });
    }
  }, [person.id, person.archivedAt, updatePerson, toast]);

  const handleSetPrimary = useCallback(async () => {
    try {
      await setPrimaryMut({ variables: { personId: person.id, clientId } });
      toast({ kind: "success", title: "Primary person updated" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to set primary", description: (err as Error).message });
    }
  }, [person.id, clientId, setPrimaryMut, toast]);

  return (
    <div className="rounded-lg hair border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar initials={getInitials(person)} size="lg" />
          <div className="min-w-0">
            <div className="text-[13.5px] font-medium truncate flex items-center gap-2">
              <Link to={`/clients/people/${person.id}`} className="hover:text-tide truncate">
                {getDisplayName(person)}
              </Link>
              {isPrimaryHere && <Badge variant="spark">Primary</Badge>}
              {person.archivedAt && <Badge variant="default">Archived</Badge>}
            </div>
            {person.title || person.businessName ? (
              <div className="text-[11.5px] text-ink-4 truncate">
                {[person.title, person.businessName].filter(Boolean).join(" · ")}
              </div>
            ) : null}
            {otherClients.length > 0 && (
              <div className="text-[11px] text-ink-5 truncate">
                Also in{" "}
                {otherClients.map((m, i) => (
                  <span key={m.id}>
                    {i > 0 && ", "}
                    <Link to={`/clients/${m.id}`} className="hover:text-tide underline decoration-ink-5/40">
                      {m.alias || m.name}
                    </Link>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
          {!isEditing ? (
            <>
              <button
                onClick={enterEdit}
                className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-3 hover:bg-paper-2 flex items-center gap-1"
              >
                <Pencil size={11} strokeWidth={2} />
                Edit
              </button>
              {!isPrimaryHere && (
                <button
                  onClick={handleSetPrimary}
                  disabled={settingPrimary}
                  className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-3 hover:bg-paper-2 flex items-center gap-1 disabled:opacity-60"
                  title="Make this person the client's primary contact"
                >
                  <Check size={11} strokeWidth={2} />
                  Make primary
                </button>
              )}
              <button
                onClick={() => onMove(person)}
                className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-3 hover:bg-paper-2 flex items-center gap-1"
                title="Move this person's membership to a different client"
              >
                <ArrowRightLeft size={11} strokeWidth={2} />
                Move
              </button>
              <button
                onClick={() => onSplit(person)}
                className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-3 hover:bg-paper-2 flex items-center gap-1"
                title="Split this person out into a new client"
              >
                <Scissors size={11} strokeWidth={2} />
                Split
              </button>
              {!onlyMembership && (
                <button
                  onClick={() => onRemove(person)}
                  className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-4 hover:bg-paper-2 hover:text-rust flex items-center gap-1"
                  title="Remove this person from this client (their other memberships remain)"
                >
                  <X size={11} strokeWidth={2} />
                  Remove
                </button>
              )}
              <button
                onClick={handleArchiveToggle}
                className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-4 hover:bg-paper-2 flex items-center gap-1"
                title={person.archivedAt ? "Restore this person" : "Archive this person (the client stays active)"}
              >
                {person.archivedAt ? (
                  <ArchiveRestore size={11} strokeWidth={2} />
                ) : (
                  <Archive size={11} strokeWidth={2} />
                )}
                {person.archivedAt ? "Restore" : "Archive"}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="px-2.5 py-1 rounded-md hair border text-[11.5px] text-ink-3 hover:bg-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-2.5 py-1 rounded-md bg-tide text-paper-hi text-[11.5px] font-medium hover:bg-tide-hi disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
          <div>
            <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Name <span className="text-rust">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide"
            />
          </div>
          <div>
            <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Email <span className="text-rust">*</span>
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide"
            />
          </div>
          <div>
            <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Phone
            </label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide"
            />
          </div>
          <div>
            <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Business name
            </label>
            <input
              value={business}
              onChange={(e) => setBusiness(e.target.value)}
              className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide"
            />
          </div>
          <div>
            <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Title
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Email
            </dt>
            <dd className="text-[13px] text-ink-2 truncate">
              <a href={`mailto:${person.email}`} className="hover:text-tide flex items-center gap-1">
                <Mail size={12} strokeWidth={2} className="text-ink-4 flex-shrink-0" />
                {person.email}
              </a>
            </dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Phone
            </dt>
            <dd className="text-[13px] text-ink-2">
              {person.phoneNumber ? (
                <a href={`tel:${person.phoneNumber}`} className="hover:text-tide flex items-center gap-1">
                  <Phone size={12} strokeWidth={2} className="text-ink-4 flex-shrink-0" />
                  {person.phoneNumber}
                </a>
              ) : (
                <span className="text-ink-5">--</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Business
            </dt>
            <dd className="text-[13px] text-ink-2 truncate">
              {person.businessName ? (
                <span className="flex items-center gap-1">
                  <Building2 size={12} strokeWidth={2} className="text-ink-4 flex-shrink-0" />
                  {person.businessName}
                </span>
              ) : (
                <span className="text-ink-5">--</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
              Title
            </dt>
            <dd className="text-[13px] text-ink-2 truncate">
              {person.title ? (
                <span className="flex items-center gap-1">
                  <Briefcase size={12} strokeWidth={2} className="text-ink-4 flex-shrink-0" />
                  {person.title}
                </span>
              ) : (
                <span className="text-ink-5">--</span>
              )}
            </dd>
          </div>
        </div>
      )}

      {/* Per-person tags */}
      <div className="mt-3 flex items-start gap-3">
        <dt className="text-[11px] text-ink-4 uppercase tracking-wider font-medium pt-2 flex-shrink-0">
          Tags
        </dt>
        <dd className="flex-1 min-w-0">
          <TagEditor
            value={person.tags ?? []}
            vocabulary={tagVocabulary}
            onChange={handleTagsChange}
          />
        </dd>
      </div>
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────

export function ClientProfile() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToastPush();

  // "Back to clients" returns to the list with the filters/search/sort/page the
  // user left. The list passes its query string via router state on the way in;
  // we rebuild it into a real URL so this stays an ordinary link (cmd/middle-
  // click, pointer cursor, openable in a new tab). Falls back to the bare list
  // on a direct link / fresh tab, where there's no incoming state.
  const fromList = (location.state as { from?: string } | null)?.from;
  const backTo = fromList ? `/clients?${fromList}` : "/clients";
  const { user } = useAuth();

  const [activeFilter, setActiveFilter] = useState<ActivityFilter>("all");
  const [isEditing, setIsEditing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  // Move / split / remove / merge state
  const [movePerson, setMovePerson] = useState<PersonRow | null>(null);
  const [splitPerson, setSplitPerson] = useState<PersonRow | null>(null);
  const [removePerson, setRemovePerson] = useState<PersonRow | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);

  // Edit form state (the Client record itself)
  const [editName, setEditName] = useState("");
  const [editAlias, setEditAlias] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // Note form state
  const [newNoteBody, setNewNoteBody] = useState("");
  const [noteContactId, setNoteContactId] = useState<string>("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteBody, setEditingNoteBody] = useState("");
  const [deleteConfirmNoteId, setDeleteConfirmNoteId] = useState<string | null>(null);
  // Expanded note in timeline (for viewing full body)
  const [expandedNoteId, setExpandedNoteId] = useState<string | null>(null);
  // Fee agreement modal (opened from "Agreement signed" events)
  const [agreementBookingId, setAgreementBookingId] = useState<string | null>(null);

  // ── Queries ──────────────────────────────────────────────────────

  const { data, loading, error, refetch: refetchDetail } = useQuery(GET_CLIENT_DETAIL, {
    variables: { id },
    skip: !id,
    fetchPolicy: "cache-and-network",
  });

  const client: DetailClient | null = data?.Client ?? null;
  const contacts: PersonRow[] = useMemo(
    () => client?.people ?? [],
    [client],
  );

  const contactIds = useMemo(() => contacts.map((c) => c.id), [contacts]);
  const contactIdsKey = contactIds.join(",");

  // Notes are per-contact; the profile aggregates them across the Client's
  // contacts via the customerIds filter.
  const notesVariables = useMemo(
    () => ({ page: 0, perPage: 200, filter: { personIds: contactIds } }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactIdsKey],
  );

  const { data: notesData, loading: notesLoading } = useQuery(GET_CLIENT_NOTES, {
    variables: notesVariables,
    skip: contactIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const notes: ClientNote[] = notesData?.allPersonNotes ?? [];

  // Tag vocabulary for autocomplete
  const { data: tagsVocabData } = useQuery(GET_ALL_PERSON_TAGS, {
    fetchPolicy: "cache-and-network",
  });
  const tagVocabulary: TagItem[] = tagsVocabData?.allPersonTags ?? [];

  // Booking IDs come from the Client's own bookings (attributed at creation
  // time — Client-stable, so contacts moving elsewhere don't take history).
  const bookingIds = useMemo(() => {
    if (!client?.bookings) return [];
    return client.bookings
      .filter((b: { deletedAt: string | null }) => b.deletedAt == null)
      .map((b: { id: string }) => b.id);
  }, [client]);

  const hasBookings = bookingIds.length > 0;

  const { data: paymentsData, loading: paymentsLoading } = useQuery(GET_BOOKING_PAYMENTS, {
    variables: {
      page: 0,
      perPage: 200,
      filter: { bookingIds },
    },
    skip: !hasBookings,
    fetchPolicy: "cache-and-network",
  });

  const { data: movesData, loading: movesLoading } = useQuery(GET_BOOKING_MOVES, {
    variables: {
      page: 0,
      perPage: 200,
      filter: { bookingIds },
    },
    skip: !hasBookings,
    fetchPolicy: "cache-and-network",
  });

  const { data: closuresData, loading: closuresLoading } = useQuery(GET_BOOKING_CLOSURES, {
    variables: {
      page: 0,
      perPage: 200,
      filter: { bookingIds },
    },
    skip: !hasBookings,
    fetchPolicy: "cache-and-network",
  });

  const { data: signaturesData, loading: signaturesLoading } = useQuery(GET_BOOKING_SIGNATURES, {
    variables: {
      page: 0,
      perPage: 200,
      filter: { bookingIds },
    },
    skip: !hasBookings,
    fetchPolicy: "cache-and-network",
  });

  const payments: RawPayment[] = paymentsData?.allPayments ?? [];
  const moves: RawBookingMove[] = movesData?.allBookingMoves ?? [];
  const closures: RawBookingClosure[] = closuresData?.allBookingClosures ?? [];
  const signatures: RawBookingSignature[] = signaturesData?.allBookingSignatures ?? [];

  // ── Mutations ────────────────────────────────────────────────────

  // The list page re-queries on mount (cache-and-network), so profile edits
  // refetch only the detail — a variables-less GET_CLIENTS refetch would run
  // an unpaginated allClients (stats + nested contacts) the list never reads.
  const detailRefetch = useMemo(
    () => [{ query: GET_CLIENT_DETAIL, variables: { id } }],
    [id],
  );

  // Contact edits also refresh the tag vocabulary: find-or-created tags
  // must show up in the next TagEditor autocomplete without a remount.
  const contactRefetch = useMemo(
    () => [
      { query: GET_CLIENT_DETAIL, variables: { id } },
      { query: GET_ALL_PERSON_TAGS },
    ],
    [id],
  );

  const [updateClient, { loading: saving }] = useMutation(UPDATE_CLIENT, {
    refetchQueries: detailRefetch,
  });

  const [movePersonMut, { loading: moving }] = useMutation(MOVE_PERSON, {
    refetchQueries: detailRefetch,
  });

  const [splitPersonMut, { loading: splitting }] = useMutation(SPLIT_PERSON);

  const [removePersonMut, { loading: removing }] = useMutation(REMOVE_PERSON_FROM_CLIENT, {
    refetchQueries: detailRefetch,
  });

  const [mergeClientsMut, { loading: merging }] = useMutation(MERGE_CLIENTS);

  const noteRefetch = useMemo(
    () => [{ query: GET_CLIENT_NOTES, variables: notesVariables }],
    [notesVariables],
  );

  const [createNote, { loading: creatingNote }] = useMutation(CREATE_PERSON_NOTE, {
    refetchQueries: noteRefetch,
  });

  const [updateNote] = useMutation(UPDATE_PERSON_NOTE, {
    refetchQueries: noteRefetch,
  });

  const [deleteNote] = useMutation(DELETE_PERSON_NOTE, {
    refetchQueries: noteRefetch,
  });

  // Default note target: the primary contact. Reset when the client changes.
  useEffect(() => {
    const first = contacts.find((c) => !c.archivedAt) ?? contacts[0];
    setNoteContactId(first?.id ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactIdsKey]);

  // ── Edit mode (Client record) ────────────────────────────────────

  const enterEditMode = useCallback(() => {
    if (!client) return;
    setEditName(client.name);
    setEditAlias(client.alias ?? "");
    setEditNotes(client.notes ?? "");
    setIsEditing(true);
  }, [client]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editName.trim()) {
      toast({ kind: "error", title: "Name is required." });
      return;
    }
    try {
      await updateClient({
        variables: {
          id,
          name: editName.trim(),
          alias: editAlias.trim() || null,
          notes: editNotes.trim() || null,
        },
      });
      setIsEditing(false);
      toast({ kind: "success", title: "Client updated" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to save", description: (err as Error).message });
    }
  }, [id, editName, editAlias, editNotes, updateClient, toast]);

  // ── Archive / Unarchive ──────────────────────────────────────────

  const handleArchive = useCallback(async () => {
    try {
      await updateClient({
        variables: { id, archivedAt: new Date().toISOString() },
      });
      setArchiveConfirmOpen(false);
      toast({ kind: "success", title: "Client archived", description: `${client ? getDisplayName(client) : "Client"} has been removed from your active list.` });
    } catch (err) {
      toast({ kind: "error", title: "Failed to archive", description: (err as Error).message });
    }
  }, [id, client, updateClient, toast]);

  const handleUnarchive = useCallback(async () => {
    try {
      await updateClient({
        variables: { id, archivedAt: null },
      });
      toast({ kind: "success", title: "Client restored", description: `${client ? getDisplayName(client) : "Client"} is back on your active list.` });
    } catch (err) {
      toast({ kind: "error", title: "Failed to restore", description: (err as Error).message });
    }
  }, [id, client, updateClient, toast]);

  // ── Move / split / merge ─────────────────────────────────────────

  const handleMoveConfirm = useCallback(
    async (targetClientId: string, targetName: string) => {
      if (!movePerson || !id) return;
      try {
        await movePersonMut({
          variables: { personId: movePerson.id, fromClientId: id, targetClientId },
        });
        setMovePerson(null);
        toast({
          kind: "success",
          title: "Person moved",
          description: `${getDisplayName(movePerson)} now belongs to ${targetName}. Past sessions and payments stay with this client.`,
        });
      } catch (err) {
        toast({ kind: "error", title: "Failed to move person", description: (err as Error).message });
      }
    },
    [movePerson, id, movePersonMut, toast],
  );

  const handleSplitConfirm = useCallback(async () => {
    if (!splitPerson || !id) return;
    try {
      const { data: splitData } = await splitPersonMut({
        variables: { personId: splitPerson.id, fromClientId: id },
      });
      const newClient = splitData?.splitPersonIntoNewClient;
      setSplitPerson(null);
      toast({
        kind: "success",
        title: "Split into a new client",
        description: `${getDisplayName(splitPerson)} is now their own client. History stays here.`,
      });
      if (newClient?.id) {
        navigate(`/clients/${newClient.id}`);
      }
    } catch (err) {
      toast({ kind: "error", title: "Failed to split person", description: (err as Error).message });
    }
  }, [splitPerson, id, splitPersonMut, toast, navigate]);

  const handleRemoveConfirm = useCallback(async () => {
    if (!removePerson || !id) return;
    try {
      await removePersonMut({
        variables: { personId: removePerson.id, clientId: id },
      });
      setRemovePerson(null);
      toast({
        kind: "success",
        title: "Person removed from this client",
        description: `${getDisplayName(removePerson)} keeps their other memberships. History stays here.`,
      });
    } catch (err) {
      toast({ kind: "error", title: "Failed to remove person", description: (err as Error).message });
    }
  }, [removePerson, id, removePersonMut, toast]);

  const handleMergeConfirm = useCallback(
    async (targetId: string, targetName: string) => {
      try {
        await mergeClientsMut({
          variables: { sourceId: id, targetId },
        });
        setMergeOpen(false);
        toast({
          kind: "success",
          title: "Clients merged",
          description: `Contacts, sessions, and payments moved to ${targetName}. This record was archived.`,
        });
        navigate(`/clients/${targetId}`);
      } catch (err) {
        toast({ kind: "error", title: "Failed to merge clients", description: (err as Error).message });
      }
    },
    [id, mergeClientsMut, toast, navigate],
  );

  // ── Notes ────────────────────────────────────────────────────────

  const handleAddNote = useCallback(async () => {
    if (!newNoteBody.trim() || !noteContactId) return;
    try {
      await createNote({ variables: { personId: noteContactId, body: newNoteBody.trim() } });
      setNewNoteBody("");
      toast({ kind: "success", title: "Note added" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to add note", description: (err as Error).message });
    }
  }, [noteContactId, newNoteBody, createNote, toast]);

  const handleUpdateNote = useCallback(async (noteId: string) => {
    if (!editingNoteBody.trim()) return;
    try {
      await updateNote({ variables: { id: noteId, body: editingNoteBody.trim() } });
      setEditingNoteId(null);
      toast({ kind: "success", title: "Note updated" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to update note", description: (err as Error).message });
    }
  }, [editingNoteBody, updateNote, toast]);

  const handleDeleteNote = useCallback(async (noteId: string) => {
    try {
      await deleteNote({ variables: { id: noteId } });
      setDeleteConfirmNoteId(null);
      setExpandedNoteId(null);
      toast({ kind: "success", title: "Note deleted" });
    } catch (err) {
      toast({ kind: "error", title: "Failed to delete note", description: (err as Error).message });
    }
  }, [deleteNote, toast]);

  // ── Fee agreement modal ──────────────────────────────────────────

  useEffect(() => {
    if (!agreementBookingId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAgreementBookingId(null);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [agreementBookingId]);

  // ── Derived data ─────────────────────────────────────────────────

  const status = client ? deriveStatus(client) : "lead";
  const displayName = client ? getDisplayName(client) : "";
  const secondaryName = client ? getSecondaryName(client) : null;
  const initials = client ? getInitials(client) : "";
  const isArchived = !!client?.archivedAt;
  const firstContact = client ? primaryContact(client) : null;

  // Contact-name lookup so multi-contact clients can label notes.
  const contactNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of contacts) m.set(c.id, getDisplayName(c));
    return m;
  }, [contacts]);

  // Activity timeline
  const allEvents = useMemo(() => {
    if (!client) return [];
    return deriveActivityTimeline(
      client,
      client.bookings ?? [],
      notes,
      payments,
      moves,
      closures,
      signatures,
      contacts.length > 1 ? contactNames : undefined,
    );
  }, [client, notes, payments, moves, closures, signatures, contacts.length, contactNames]);

  const categoryCounts = useMemo(() => countByCategory(allEvents), [allEvents]);

  const filteredEvents = useMemo(() => {
    if (activeFilter === "all") return allEvents;
    // When filtering by a category, include lifecycle events only in "all"
    return allEvents.filter((ev) => ev.category === activeFilter);
  }, [allEvents, activeFilter]);

  const dayGroups = useMemo(() => groupEventsByDay(filteredEvents), [filteredEvents]);

  // Filter chips configuration
  const filterChips: {
    key: ActivityFilter;
    label: string;
    count?: number;
    disabled?: boolean;
  }[] = [
    { key: "all", label: "All", count: allEvents.length },
    { key: "sessions", label: "Sessions", count: categoryCounts.sessions },
    { key: "payments", label: "Payments", count: categoryCounts.payments },
    { key: "agreements", label: "Agreements", count: categoryCounts.agreements },
    { key: "notes", label: "Notes", count: categoryCounts.notes },
  ];

  // ── Render ───────────────────────────────────────────────────────

  if (loading && !client) {
    return (
      <div className="px-4 md:px-7 pt-6 pb-20 max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-4 w-24 bg-canvas rounded" />
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-md bg-canvas" />
            <div className="space-y-2">
              <div className="h-6 w-48 bg-canvas rounded" />
              <div className="h-4 w-32 bg-canvas rounded" />
            </div>
          </div>
          <div className="h-32 bg-canvas rounded-lg" />
          <div className="h-64 bg-canvas rounded-lg" />
        </div>
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="pb-20">
        <PageHeader heading={false} back={{ to: backTo, label: "clients" }} />
        <div className="mx-auto mt-2 max-w-4xl bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <AlertCircle size={24} strokeWidth={1.5} className="mx-auto text-rust mb-2" />
          <p className="text-ink-2 font-medium">Failed to load client</p>
          <p className="text-ink-4 text-[13px] mt-1">Something went wrong. Please try again.</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="pb-20">
        <PageHeader heading={false} back={{ to: backTo, label: "clients" }} />
        <div className="mx-auto mt-2 max-w-4xl bg-paper-hi rounded-lg shadow-card p-8 text-center">
          <Users size={24} strokeWidth={1.5} className="mx-auto text-ink-4 mb-2" />
          <p className="text-ink-2 font-medium">Client not found</p>
          <p className="text-ink-4 text-[13px] mt-1">
            This client may have been removed.{" "}
            <Link to="/clients" className="text-tide hover:underline">Return to the client list.</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      {/* Back link — returns to the list with its preserved filters/search/sort/page */}
      <PageHeader heading={false} back={{ to: backTo, label: "clients" }} />

      <div className="px-4 md:px-7 max-w-4xl mx-auto">

      {/* ── Profile header ────────────────────────────────────────── */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden mb-4">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar
                initials={initials}
                size="lg"
                className="!w-16 !h-16 !text-[20px] !rounded-md"
              />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="font-display text-[26px] font-medium leading-tight">
                    {displayName}
                  </h1>
                  {isArchived && <Badge variant="default">Archived</Badge>}
                </div>
                {secondaryName && (
                  <div className="text-[13px] text-ink-4 mb-0.5">
                    {secondaryName}
                  </div>
                )}
                <div className="flex items-center gap-2 text-[12.5px]">
                  <StatusPill status={status} />
                  <span className="text-ink-5">·</span>
                  <span className="text-ink-4">
                    Client since {formatMemberSince(client.createdAt)}
                  </span>
                  <span className="text-ink-5">·</span>
                  <span className="text-ink-4">
                    {client.peopleCount} {client.peopleCount === 1 ? "person" : "people"}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
              {!isEditing && (
                <>
                  <button
                    onClick={() => setCreateOpen(true)}
                    disabled={!firstContact}
                    className="px-3 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-hi flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus size={12} strokeWidth={2} />
                    New session
                  </button>
                  {firstContact && (
                    <a
                      href={`mailto:${firstContact.email}`}
                      className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
                      title="Email client"
                    >
                      <Mail size={12} strokeWidth={2} />
                      Email
                    </a>
                  )}
                  <button
                    onClick={enterEditMode}
                    className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
                  >
                    <Pencil size={12} strokeWidth={2} />
                    Edit
                  </button>
                  <button
                    onClick={() => setMergeOpen(true)}
                    className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
                    title="Merge this client into another"
                  >
                    <Merge size={12} strokeWidth={2} />
                    Merge into...
                  </button>
                  {isArchived ? (
                    <button
                      onClick={handleUnarchive}
                      className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
                    >
                      <ArchiveRestore size={12} strokeWidth={2} />
                      Restore
                    </button>
                  ) : (
                    <button
                      onClick={() => setArchiveConfirmOpen(true)}
                      className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-4 hover:bg-paper-2 hover:text-rust flex items-center gap-1.5"
                    >
                      <Archive size={12} strokeWidth={2} />
                      Archive
                    </button>
                  )}
                </>
              )}
              {isEditing && (
                <>
                  <button
                    onClick={cancelEdit}
                    className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-3 hover:bg-paper-2 flex items-center gap-1.5"
                  >
                    <X size={12} strokeWidth={2} />
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="px-3 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium flex items-center gap-1.5 hover:bg-tide-hi disabled:opacity-60"
                  >
                    <Check size={12} strokeWidth={2} />
                    {saving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Client record fields (edit) / relationship notes ────── */}
        {isEditing ? (
          <div className="px-6 pb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                Client name <span className="text-rust">*</span>
              </label>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide"
              />
            </div>
            <div>
              <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                Alias
              </label>
              <input
                value={editAlias}
                onChange={(e) => setEditAlias(e.target.value)}
                placeholder="Display name override"
                className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide placeholder:text-ink-5"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1">
                Relationship notes
              </label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={3}
                placeholder="Context about the relationship itself (engagement terms, billing arrangements...)"
                className="w-full px-2.5 py-1.5 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide placeholder:text-ink-5 resize-none"
              />
            </div>
          </div>
        ) : client.notes ? (
          <div className="px-6 pb-5">
            <div className="rounded-md bg-canvas px-4 py-3">
              <div className="text-[11px] text-ink-4 uppercase tracking-wider font-medium mb-1 flex items-center gap-1.5">
                <FileText size={11} strokeWidth={2} />
                Relationship notes
              </div>
              <div className="text-[13px] text-ink-2 whitespace-pre-wrap break-words">
                {client.notes}
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Stats row ─────────────────────────────────────────── */}
        <div className="hair-t px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="font-display text-[22px] font-medium num">
              {client.sessionsCount}
            </div>
            <div className="text-[11px] text-ink-4 uppercase tracking-wider mt-0.5">
              {client.sessionsCount === 1 ? "Session" : "Sessions"}
            </div>
          </div>
          <div className="hair-l">
            <div className="font-display text-[22px] font-medium num">
              {formatCurrency(client.lifetimeSpend)}
            </div>
            <div className="text-[11px] text-ink-4 uppercase tracking-wider mt-0.5">
              Lifetime
            </div>
          </div>
          <div className="hair-l hidden md:block">
            <div className="text-[14px] font-medium text-ink-2 mt-1">
              {client.lastSessionAt
                ? formatSessionLabel(new Date(client.lastSessionAt))
                : "--"}
            </div>
            <div className="text-[11px] text-ink-4 uppercase tracking-wider mt-0.5">
              Last session
            </div>
          </div>
          <div className="hair-l hidden md:block">
            <div className="text-[14px] font-medium text-ink-2 mt-1">
              {client.nextSessionAt
                ? formatSessionLabel(new Date(client.nextSessionAt))
                : "--"}
            </div>
            <div className="text-[11px] text-ink-4 uppercase tracking-wider mt-0.5">
              Next up
            </div>
          </div>
        </div>
      </div>

      {/* ── People ─────────────────────────────────────────────── */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden mb-4">
        <div className="px-6 pt-5 pb-4 flex items-center justify-between">
          <h2 className="text-[14px] font-semibold">
            {contacts.length === 1 ? "Person" : `People (${contacts.length})`}
          </h2>
          <button
            onClick={() => setAddContactOpen(true)}
            className="px-3 py-1.5 rounded-md hair border text-[12.5px] text-ink-2 hover:bg-paper-2 flex items-center gap-1.5"
          >
            <UserPlus size={12} strokeWidth={2} />
            Add person
          </button>
        </div>
        <div className="px-6 pb-6 space-y-3">
          {contacts.length === 0 ? (
            <div className="text-[13px] text-ink-4">
              No people yet. Add the first person for this relationship.
            </div>
          ) : (
            contacts.map((c) => (
              <PersonCard
                key={c.id}
                person={c}
                clientId={client.id}
                tagVocabulary={tagVocabulary}
                onMove={setMovePerson}
                onSplit={setSplitPerson}
                onRemove={setRemovePerson}
                refetchQueries={contactRefetch}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Activity feed ───────────────────────────────────────── */}
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        {/* Filter chips */}
        <div className="flex items-center gap-2 px-6 pt-5 pb-4">
          {filterChips.map((chip) => {
            const isActive = activeFilter === chip.key;
            return (
              <button
                key={chip.key}
                onClick={() => !chip.disabled && setActiveFilter(chip.key)}
                disabled={chip.disabled}
                className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium transition flex items-center gap-1.5 ${
                  chip.disabled
                    ? "text-ink-5 bg-canvas cursor-not-allowed"
                    : isActive
                      ? "bg-tide text-paper-hi"
                      : "bg-canvas text-ink-3 hover:bg-paper-2 hover:text-ink-2"
                }`}
              >
                {chip.label}
                {chip.count != null && (
                  <span
                    className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                      chip.disabled
                        ? "bg-paper-2 text-ink-5"
                        : isActive
                          ? "bg-paper-hi/20 text-paper-hi"
                          : "bg-paper-2 text-ink-4"
                    }`}
                  >
                    {chip.count}
                  </span>
                )}
                {chip.disabled && (
                  <span className="text-[10px] text-ink-5">Coming soon</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Add note form (always visible; notes attach to a contact) */}
        <div className="px-6 pb-4">
          <div className="rounded-lg bg-canvas p-4">
            <textarea
              value={newNoteBody}
              onChange={(e) => setNewNoteBody(e.target.value)}
              placeholder="Add a note about this client..."
              maxLength={NOTE_MAX_CHARS}
              rows={2}
              className="w-full px-3 py-2 text-[13px] rounded-md bg-paper-hi hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5 resize-none"
            />
            <div className="flex items-center justify-between mt-2 gap-3">
              <span className="text-[11px] text-ink-5">
                {newNoteBody.length > 0 && `${newNoteBody.length} / ${NOTE_MAX_CHARS}`}
              </span>
              <div className="flex items-center gap-2">
                {contacts.length > 1 && (
                  <select
                    value={noteContactId}
                    onChange={(e) => setNoteContactId(e.target.value)}
                    className="px-2.5 py-1.5 rounded-md hair border bg-paper-hi text-[12px] text-ink-3 cursor-pointer"
                    aria-label="Contact this note is about"
                  >
                    {contacts.map((c) => (
                      <option key={c.id} value={c.id}>
                        re: {c.alias || c.name}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  onClick={handleAddNote}
                  disabled={!newNoteBody.trim() || !noteContactId || creatingNote}
                  className="px-3 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium flex items-center gap-1.5 hover:bg-tide-hi disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={12} strokeWidth={2} />
                  {creatingNote ? "Adding..." : "Add note"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline content */}
        <div className="px-6 pb-6">
          {(loading || notesLoading || paymentsLoading || movesLoading || closuresLoading || signaturesLoading) && allEvents.length === 0 ? (
            // Loading skeleton
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="w-8 h-8 rounded-full bg-canvas flex-shrink-0" />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-3 w-40 bg-canvas rounded" />
                    <div className="h-3 w-24 bg-canvas rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            // Empty state
            <div className="text-center py-10">
              <div className="text-ink-5 mb-2">
                {activeFilter === "all" ? (
                  <UserPlus size={28} strokeWidth={1.5} className="mx-auto" />
                ) : activeFilter === "sessions" ? (
                  <Calendar size={28} strokeWidth={1.5} className="mx-auto" />
                ) : activeFilter === "payments" ? (
                  <CreditCard size={28} strokeWidth={1.5} className="mx-auto" />
                ) : (
                  <MessageSquare size={28} strokeWidth={1.5} className="mx-auto" />
                )}
              </div>
              <p className="text-ink-3 text-[13px] font-medium">
                {activeFilter === "all"
                  ? "No activity yet"
                  : activeFilter === "sessions"
                    ? "No sessions yet"
                    : activeFilter === "payments"
                      ? "No payments yet"
                      : "No notes yet"}
              </p>
              <p className="text-ink-4 text-[12px] mt-1">
                {activeFilter === "all"
                  ? "Activity will appear here as sessions are booked and notes are added."
                  : activeFilter === "sessions"
                    ? "Sessions will appear here once this client has bookings."
                    : activeFilter === "payments"
                      ? "Payment history will appear here once this client has completed sessions."
                      : "Add a note above to keep track of important context."}
              </p>
            </div>
          ) : (
            // Day-grouped timeline
            <div className="space-y-6">
              {dayGroups.map((day) => (
                <div key={day.dateKey}>
                  {/* Day header */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[11.5px] font-semibold text-ink-3 uppercase tracking-wider">
                      {day.label}
                    </span>
                    <div className="flex-1 h-px bg-canvas" />
                  </div>

                  {/* Events for this day */}
                  <div className="space-y-1">
                    {day.events.map((ev) => {
                      const IconComponent = EVENT_ICONS[ev.icon];
                      const iconColor = EVENT_ICON_COLORS[ev.icon];
                      const isNote = ev.category === "notes";
                      const isExpanded = isNote && expandedNoteId === ev.noteId;
                      const isEditingThisNote = isNote && editingNoteId === ev.noteId;
                      const isDeleteConfirm = isNote && deleteConfirmNoteId === ev.noteId;
                      const isOwnNote = isNote && ev.noteAuthorId === user?.id;

                      // Note events render with inline expand/edit/delete
                      if (isNote) {
                        return (
                          <div
                            key={ev.id}
                            className="group rounded-md hover:bg-canvas transition px-3 py-2.5"
                          >
                            <div className="flex items-start gap-3">
                              <div className={`mt-0.5 flex-shrink-0 ${iconColor}`}>
                                <IconComponent size={16} strokeWidth={2} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[12.5px] font-medium text-ink-3">
                                      {ev.noteAuthorEmail}
                                    </span>
                                    {ev.noteContactName && (
                                      <span className="text-[11px] text-ink-5 truncate">
                                        re: {ev.noteContactName}
                                      </span>
                                    )}
                                    {ev.noteEdited && (
                                      <span className="text-[11px] text-ink-5">(edited)</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {isOwnNote && !isEditingThisNote && !isDeleteConfirm && (
                                      <div className="hidden group-hover:flex items-center gap-1">
                                        <button
                                          onClick={() => {
                                            setEditingNoteId(ev.noteId!);
                                            setEditingNoteBody(ev.noteBody!);
                                            setExpandedNoteId(ev.noteId!);
                                          }}
                                          className="p-1 rounded hover:bg-paper-2 text-ink-4 hover:text-ink-2"
                                          aria-label="Edit note"
                                        >
                                          <Pencil size={12} strokeWidth={2} />
                                        </button>
                                        <button
                                          onClick={() => setDeleteConfirmNoteId(ev.noteId!)}
                                          className="p-1 rounded hover:bg-paper-2 text-ink-4 hover:text-rust"
                                          aria-label="Delete note"
                                        >
                                          <Trash2 size={12} strokeWidth={2} />
                                        </button>
                                      </div>
                                    )}
                                    <span className="text-[11px] text-ink-5">
                                      {formatEventTime(ev.timestamp)}
                                    </span>
                                  </div>
                                </div>

                                {isEditingThisNote ? (
                                  <div className="mt-2">
                                    <textarea
                                      value={editingNoteBody}
                                      onChange={(e) => setEditingNoteBody(e.target.value)}
                                      maxLength={NOTE_MAX_CHARS}
                                      rows={3}
                                      className="w-full px-3 py-2 text-[13px] rounded-md bg-paper-hi hair border focus:outline-none focus:border-tide text-ink resize-none"
                                    />
                                    <div className="flex items-center justify-end gap-2 mt-2">
                                      <button
                                        onClick={() => setEditingNoteId(null)}
                                        className="px-2.5 py-1 rounded text-[12px] text-ink-3 hover:bg-paper-2"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => handleUpdateNote(ev.noteId!)}
                                        disabled={!editingNoteBody.trim()}
                                        className="px-2.5 py-1 rounded bg-tide text-paper-hi text-[12px] font-medium hover:bg-tide-hi disabled:opacity-50"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : isDeleteConfirm ? (
                                  <div className="flex items-center justify-between mt-2">
                                    <span className="text-[12.5px] text-ink-3">Delete this note?</span>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => setDeleteConfirmNoteId(null)}
                                        className="px-2.5 py-1 rounded text-[12px] text-ink-3 hover:bg-paper-2"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => handleDeleteNote(ev.noteId!)}
                                        className="px-2.5 py-1 rounded bg-rust text-paper-hi text-[12px] font-medium"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() =>
                                      setExpandedNoteId(
                                        isExpanded ? null : ev.noteId!,
                                      )
                                    }
                                    className="text-left mt-1 w-full"
                                  >
                                    <div className="text-[13px] text-ink-2 whitespace-pre-wrap break-words">
                                      {isExpanded ? ev.noteBody : ev.detail}
                                    </div>
                                    {!isExpanded && ev.noteBody && ev.noteBody.length > 80 && (
                                      <span className="text-[11.5px] text-tide mt-0.5 inline-block">
                                        Show more
                                      </span>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Non-note events (sessions, payments, lifecycle)
                      const isMuted = !!ev.muted;
                      const hasAgreement = !!ev.bookingId;
                      const content = (
                        <div className="flex items-start gap-3">
                          <div className={`mt-0.5 flex-shrink-0 ${isMuted ? "text-ink-5" : iconColor}`}>
                            <IconComponent size={16} strokeWidth={2} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className={`text-[13px] font-medium ${isMuted ? "text-ink-4" : "text-ink-2"}`}>
                                {ev.title}
                              </span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {hasAgreement && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setAgreementBookingId(ev.bookingId!);
                                    }}
                                    className="hidden group-hover:inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-tide hover:bg-tide-ss transition"
                                  >
                                    <FileText size={10} strokeWidth={2} />
                                    View agreement
                                  </button>
                                )}
                                <span className="text-[11px] text-ink-5">
                                  {formatEventTime(ev.timestamp)}
                                </span>
                              </div>
                            </div>
                            {ev.detail && (
                              <div className={`text-[12px] mt-0.5 ${isMuted ? "text-ink-5" : "text-ink-4"}`}>
                                {ev.detail}
                              </div>
                            )}
                          </div>
                        </div>
                      );

                      if (ev.linkTo) {
                        return (
                          <Link
                            key={ev.id}
                            to={ev.linkTo}
                            className="block rounded-md hover:bg-canvas transition px-3 py-2.5 group"
                          >
                            {content}
                          </Link>
                        );
                      }

                      return (
                        <div
                          key={ev.id}
                          className="rounded-md px-3 py-2.5"
                        >
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Archive confirmation dialog ──────────────────────────── */}
      {archiveConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setArchiveConfirmOpen(false)} />
          <div className="relative bg-paper-hi rounded-lg shadow-lift w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-[16px] font-medium mb-2">
              Archive {displayName}?
            </h3>
            <p className="text-[13px] text-ink-3 mb-5">
              They will be hidden from your active client list. You can restore them at any time.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setArchiveConfirmOpen(false)}
                className="px-4 py-2 rounded-md hair border text-[13px] text-ink-2 hover:bg-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={handleArchive}
                className="px-4 py-2 rounded-md bg-rust text-paper-hi text-[13px] font-medium"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Split confirmation dialog ────────────────────────────── */}
      {splitPerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setSplitPerson(null)} />
          <div className="relative bg-paper-hi rounded-lg shadow-lift w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-[16px] font-medium mb-2">
              Split {getDisplayName(splitPerson)} into a new client?
            </h3>
            <p className="text-[13px] text-ink-3 mb-5">
              Their membership here moves to a brand-new client. Past
              sessions, payments, and agreements stay with {displayName}.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setSplitPerson(null)}
                className="px-4 py-2 rounded-md hair border text-[13px] text-ink-2 hover:bg-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSplitConfirm}
                disabled={splitting}
                className="px-4 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-hi disabled:opacity-60"
              >
                {splitting ? "Splitting..." : "Split"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Remove-from-client confirmation dialog ───────────────── */}
      {removePerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setRemovePerson(null)} />
          <div className="relative bg-paper-hi rounded-lg shadow-lift w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-[16px] font-medium mb-2">
              Remove {getDisplayName(removePerson)} from {displayName}?
            </h3>
            <p className="text-[13px] text-ink-3 mb-5">
              They keep their other client memberships, and past sessions and
              payments stay attributed here.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setRemovePerson(null)}
                className="px-4 py-2 rounded-md hair border text-[13px] text-ink-2 hover:bg-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveConfirm}
                disabled={removing}
                className="px-4 py-2 rounded-md bg-rust text-paper-hi text-[13px] font-medium disabled:opacity-60"
              >
                {removing ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Move person picker ───────────────────────────────────── */}
      <ClientPickerModal
        open={!!movePerson}
        title={`Move ${movePerson ? getDisplayName(movePerson) : "person"} to...`}
        confirmLabel="Move person"
        confirmNote={(target) =>
          `Their membership here moves to ${target}. Past sessions and payments stay with ${displayName}.`
        }
        excludeId={client.id}
        busy={moving}
        onClose={() => setMovePerson(null)}
        onConfirm={handleMoveConfirm}
      />

      {/* ── Merge picker ─────────────────────────────────────────── */}
      <ClientPickerModal
        open={mergeOpen}
        title={`Merge ${displayName} into...`}
        confirmLabel="Merge clients"
        confirmNote={(target) =>
          `${target} absorbs all contacts, sessions, and payments. ${displayName} will be archived. This cannot be undone from the portal.`
        }
        excludeId={client.id}
        busy={merging}
        onClose={() => setMergeOpen(false)}
        onConfirm={handleMergeConfirm}
      />

      {/* ── Fee agreement viewer modal ─────────────────────────────── */}
      {agreementBookingId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-ink/40 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Fee agreement"
          onClick={(e) => {
            if (e.target === e.currentTarget) setAgreementBookingId(null);
          }}
        >
          <div className="bg-paper-hi rounded-lg shadow-card w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
            <div className="px-5 py-3 hair-b flex items-center justify-between">
              <div>
                <div className="text-[13.5px] font-semibold text-ink">
                  Fee agreement
                </div>
                <div className="text-[11.5px] text-ink-4">
                  {displayName}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAgreementBookingId(null)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-ink-4 hover:bg-paper-2 hover:text-ink-2"
                aria-label="Close"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
            <div className="flex-1 min-h-0 p-4">
              <FeeAgreementViewer
                bookingId={agreementBookingId}
                height="100%"
                className="h-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Add person modal ─────────────────────────────────────── */}
      <AddClientModal
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        clientId={client.id}
        clientName={displayName}
        onCreated={() => refetchDetail()}
      />

      {firstContact && (
        <ManualSessionModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          presetCustomerId={firstContact.id}
          onCreated={() => refetchDetail()}
        />
      )}
      </div>
    </div>
  );
}
